import type { Address } from '@squire/api-client/models/Address';
import type { AddressComponents } from '@squire/api-client/models/types';

/**
 * Converts an Address object to a formatted string
 * @param address - The address object to format
 * @param options - Formatting options
 * @returns Formatted address string
 */
export function formatAddressToString(
  address: Address | AddressComponents | null | undefined,
  options: {
    includeCountry?: boolean;
    includeNeighborhood?: boolean;
    separator?: string;
  } = {},
): string {
  if (!address) {
    return '';
  }

  const {
    includeCountry = false,
    includeNeighborhood = false,
    separator = ', ',
  } = options;

  const parts: string[] = [];

  // Add street address
  if (address.street) {
    parts.push(address.street);
  }

  // Add street2 if present
  if (address.street2) {
    parts.push(address.street2);
  }

  // Add neighborhood if requested and present
  if (includeNeighborhood && address.neighborhood) {
    parts.push(address.neighborhood);
  }

  // Add city, state, zip combination
  const cityStateZip: string[] = [];
  if (address.city) {
    cityStateZip.push(address.city);
  }
  if (address.state) {
    cityStateZip.push(address.state);
  }
  if (address.zip) {
    cityStateZip.push(address.zip);
  }

  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(', '));
  }

  // Add country if requested and present
  if (includeCountry && address.country) {
    parts.push(address.country);
  }

  return parts.join(separator);
}

/**
 * Converts an Address object to a short formatted string (city, state, zip only)
 * @param address - The address object to format
 * @returns Short formatted address string
 */
export function formatAddressToShortString(
  address: Address | AddressComponents | null | undefined,
): string {
  if (!address) {
    return '';
  }

  const parts: string[] = [];

  if (address.city) {
    parts.push(address.city);
  }
  if (address.state) {
    parts.push(address.state);
  }
  if (address.zip) {
    parts.push(address.zip);
  }

  return parts.join(', ');
}

/**
 * Converts an Address object to a street address string (street and street2 only)
 * @param address - The address object to format
 * @returns Street address string
 */
export function formatAddressToStreetString(
  address: Address | AddressComponents | null | undefined,
): string {
  if (!address) {
    return '';
  }

  const parts: string[] = [];

  if (address.street) {
    parts.push(address.street);
  }
  if (address.street2) {
    parts.push(address.street2);
  }

  return parts.join(', ');
}
